import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock, MapPin, Users, Images } from "lucide-react";
import { api } from "@/lib/api";
import { tanggalPanjang } from "@/lib/format";
import { STATUS, waktuLabel, fileUrl } from "@/lib/activity";
import { Header } from "@/components/landing/Header";
import { ContactSection } from "@/components/landing/ContactSection";
import { CategoryBanner } from "@/components/activity/CategoryBanner";
import { PeopleList } from "@/components/activity/PeopleList";
import { ActivityExtras } from "@/components/activity/ActivityExtras";
import { ShareButtons } from "@/components/activity/ShareButtons";

const Info = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <div className="h-10 w-10 rounded-2xl bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0"><Icon size={18} /></div>
    <div><p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">{label}</p><p className="font-semibold text-emerald-950">{value}</p></div>
  </div>
);

export default function KegiatanDetail() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [a, setA] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get(`/activities/${id}`).then((r) => setA(r.data)).catch(() => setError("Kegiatan tidak ditemukan"));
  }, [id]);

  const st = a ? STATUS[a.status] || STATUS.terjadwal : null;

  return (
    <div className="min-h-screen bg-background" data-testid="activity-detail-page">
      <Header profile={profile} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <Link to="/kegiatan" className="inline-flex items-center gap-2 text-sm text-emerald-800 hover:underline" data-testid="activity-back"><ArrowLeft size={16} /> Semua kegiatan</Link>
        {error && <p className="rounded-3xl bg-red-50 text-red-700 p-6" data-testid="activity-not-found">{error}</p>}
        {a && (
          <>
            <CategoryBanner category={a.category} status={a.status} title={a.title} image={a.banner_path}>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${st.tone}`} data-testid="activity-detail-status">{st.label}</span>
                <span className="text-sm text-white/85">{tanggalPanjang(a.date)} · {waktuLabel(a)}</span>
              </div>
            </CategoryBanner>
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="rounded-3xl bg-white border border-emerald-900/10 p-6 grid sm:grid-cols-3 gap-5">
                  <Info icon={CalendarDays} label="Tanggal" value={tanggalPanjang(a.date)} testId="detail-date" />
                  <Info icon={Clock} label="Waktu" value={waktuLabel(a)} testId="detail-time" />
                  <Info icon={MapPin} label="Lokasi" value={a.location || "Masjid Al-Iman"} testId="detail-location" />
                </div>
                {a.description && (
                  <div className="rounded-3xl bg-white border border-emerald-900/10 p-6" data-testid="detail-description">
                    <h3 className="font-semibold text-emerald-950">Deskripsi Kegiatan</h3>
                    <p className="mt-3 text-slate-700 leading-relaxed whitespace-pre-line">{a.description}</p>
                  </div>
                )}
                {a.status === "dibatalkan" && a.cancel_reason && (
                  <div className="rounded-3xl bg-red-50 border border-red-200 p-6 text-red-800" data-testid="detail-cancel-reason">
                    <h3 className="font-semibold">Kegiatan Dibatalkan</h3>
                    <p className="mt-2 text-sm">Alasan: {a.cancel_reason}</p>
                  </div>
                )}
                <ActivityExtras a={a} />
                {a.status === "selesai" && a.report && (
                  <div className="rounded-3xl bg-white border border-emerald-900/10 p-6" data-testid="detail-report">
                    <h3 className="font-semibold text-emerald-950 flex items-center gap-2"><Images size={16} /> Laporan & Dokumentasi</h3>
                    {a.report.summary && <p className="mt-3 text-slate-700 leading-relaxed whitespace-pre-line">{a.report.summary}</p>}
                    <p className="mt-3 text-sm text-emerald-800 inline-flex items-center gap-2"><Users size={14} /> {a.report.attendance_count || 0} jamaah hadir</p>
                    {a.report.notes && <p className="mt-2 text-xs text-slate-500">Catatan: {a.report.notes}</p>}
                    {a.report.photos?.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {a.report.photos.map((p) => <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} alt="Dokumentasi" className="aspect-square object-cover rounded-2xl border border-emerald-900/10" /></a>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-6">
                <PeopleList activity={a} />
                <div className="rounded-3xl emerald-bg pattern-overlay text-white p-6">
                  <h3 className="font-semibold">Bagikan Kegiatan</h3>
                  <p className="text-sm text-emerald-200 mt-1 mb-4">Ajak keluarga dan tetangga untuk hadir.</p>
                  <ShareButtons activity={a} dark />
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      <ContactSection profile={profile} />
    </div>
  );
}
